(() => {
  const VERSION = "7";
  const SELECTOR = "[data-fs-v2-reveal]";
  const STORY_SELECTOR = "[data-fs-v2-story]";

  if (window.FishingSections?.version === VERSION) {
    window.FishingSections.init(document);
    return;
  }

  const observed = new WeakSet();
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let observer = null;

  const collect = (scope, selector) => {
    const nodes = [];
    if (scope?.matches?.(selector)) nodes.push(scope);
    scope?.querySelectorAll?.(selector).forEach((node) => nodes.push(node));
    return nodes;
  };

  const show = (node) => {
    node.classList.add("is-visible");
    observer?.unobserve(node);
  };

  const getObserver = () => {
    if (observer || !("IntersectionObserver" in window)) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) show(entry.target);
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -5% 0px" });
    return observer;
  };

  const reveal = (scope = document) => {
    const nodes = collect(scope, SELECTOR);

    if (reduceMotion.matches || !("IntersectionObserver" in window) || document.documentElement.classList.contains("shopify-design-mode")) {
      nodes.forEach(show);
      return;
    }

    document.documentElement.classList.add("fs-v2-motion-ready");
    const activeObserver = getObserver();

    nodes.forEach((node) => {
      if (node.classList.contains("is-visible") || observed.has(node)) return;
      const rect = node.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.94 && rect.bottom > 0) {
        show(node);
      } else {
        observed.add(node);
        activeObserver?.observe(node);
      }
    });
  };

  const initFooter = (scope = document) => {
    collect(scope, "[data-fs-v2-footer-toggle]").forEach((toggle) => {
      if (toggle.dataset.fsReady === VERSION) return;
      toggle.dataset.fsReady = VERSION;
      toggle.addEventListener("click", () => {
        const group = toggle.closest("[data-fs-v2-footer-group]");
        const open = toggle.getAttribute("aria-expanded") === "true";
        toggle.setAttribute("aria-expanded", String(!open));
        group?.classList.toggle("is-open", !open);
      });
    });
  };

  const atlasTargetLeft = (track, card) => {
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    return track.scrollLeft + cardRect.left - trackRect.left;
  };

  const scrollAtlasTo = (track, card, behavior = "smooth") => {
    if (!track || !card) return;
    track.scrollTo({
      left: atlasTargetLeft(track, card),
      behavior: reduceMotion.matches ? "auto" : behavior
    });
  };

  const initAtlas = (scope = document) => {
    collect(scope, "[data-fs-v2-atlas]").forEach((atlas) => {
      const track = atlas.querySelector("[data-fs-v2-atlas-track]");
      const cards = Array.from(atlas.querySelectorAll("[data-fs-v2-atlas-slide]"));
      const controls = atlas.querySelector("[data-fs-v2-atlas-controls]");
      const progress = atlas.querySelector("[data-fs-v2-atlas-progress]");
      const current = atlas.querySelector("[data-fs-v2-atlas-current]");
      const previous = atlas.querySelector("[data-fs-v2-atlas-prev]");
      const next = atlas.querySelector("[data-fs-v2-atlas-next]");
      if (!track || cards.length < 2 || !controls) return;
      if (atlas.dataset.fsAtlasReady === VERSION) return;
      atlas.dataset.fsAtlasReady = VERSION;

      let activeIndex = 0;
      let scrollFrame = 0;

      const closestIndex = () => cards.reduce((best, card, index) => {
        const distance = Math.abs(atlasTargetLeft(track, card) - track.scrollLeft);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity }).index;

      const update = () => {
        activeIndex = closestIndex();
        const position = String(activeIndex + 1).padStart(2, "0");
        const percentage = `${((activeIndex + 1) / cards.length) * 100}%`;

        atlas.style.setProperty("--fs-v2-atlas-progress", percentage);
        if (current) current.textContent = position;
        if (previous) previous.disabled = activeIndex === 0;
        if (next) next.disabled = activeIndex === cards.length - 1;
        cards.forEach((card, index) => {
          if (index === activeIndex) card.setAttribute("aria-current", "true");
          else card.removeAttribute("aria-current");
        });
      };

      const goTo = (index) => {
        const targetIndex = Math.max(0, Math.min(cards.length - 1, index));
        scrollAtlasTo(track, cards[targetIndex]);
      };

      track.addEventListener("scroll", () => {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(update);
      }, { passive: true });

      track.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          goTo(activeIndex - 1);
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          goTo(activeIndex + 1);
        } else if (event.key === "Home") {
          event.preventDefault();
          goTo(0);
        } else if (event.key === "End") {
          event.preventDefault();
          goTo(cards.length - 1);
        }
      });

      previous?.addEventListener("click", () => goTo(activeIndex - 1));
      next?.addEventListener("click", () => goTo(activeIndex + 1));
      update();
    });
  };

  const initStorySlideshows = (scope = document) => {
    collect(scope, STORY_SELECTOR).forEach((story) => {
      if (story.dataset.fsStoryReady === VERSION) return;
      story.fsV2StoryCleanup?.();
      story.dataset.fsStoryReady = VERSION;

      const slides = Array.from(story.querySelectorAll("[data-fs-v2-story-slide]"));
      const toggle = story.querySelector("[data-fs-v2-story-toggle]");
      const mobileViewport = window.matchMedia("(max-width: 749px)");

      if (slides.length === 0) return;

      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));
      let timer = 0;
      let userPaused = false;
      let inView = true;
      let intersectionObserver = null;

      const clearTimer = () => {
        window.clearTimeout(timer);
        timer = 0;
      };

      const setActive = (index) => {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === activeIndex;
          slide.classList.toggle("is-active", active);
          slide.setAttribute("aria-hidden", String(!active));
        });
      };

      const canAutoplay = () => (
        slides.length > 1
        && mobileViewport.matches
        && !reduceMotion.matches
        && !userPaused
        && inView
        && !document.hidden
      );

      const updateToggle = () => {
        if (!toggle) return;
        toggle.classList.toggle("is-paused", userPaused);
        toggle.setAttribute("aria-pressed", String(userPaused));
        toggle.setAttribute("aria-label", userPaused ? "Resume image rotation" : "Pause image rotation");
      };

      const schedule = () => {
        clearTimer();
        if (!canAutoplay()) return;
        timer = window.setTimeout(() => {
          setActive(activeIndex + 1);
          schedule();
        }, 3000);
      };

      const handleToggle = () => {
        userPaused = !userPaused;
        updateToggle();
        schedule();
      };

      const handleEnvironmentChange = () => {
        if (reduceMotion.matches) setActive(0);
        schedule();
      };

      const selectEditorSlide = (slide) => {
        const selectedIndex = slides.indexOf(slide);
        if (selectedIndex < 0) return;
        userPaused = true;
        setActive(selectedIndex);
        updateToggle();
        clearTimer();
      };

      toggle?.addEventListener("click", handleToggle);
      document.addEventListener("visibilitychange", handleEnvironmentChange);
      mobileViewport.addEventListener?.("change", handleEnvironmentChange);
      reduceMotion.addEventListener?.("change", handleEnvironmentChange);

      if ("IntersectionObserver" in window) {
        const rect = story.getBoundingClientRect();
        inView = rect.bottom > 0 && rect.top < window.innerHeight;
        intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target !== story) return;
            inView = entry.isIntersecting;
            schedule();
          });
        }, { threshold: 0.2 });
        intersectionObserver.observe(story);
      }

      story.fsV2StorySelect = selectEditorSlide;
      story.fsV2StoryCleanup = () => {
        clearTimer();
        intersectionObserver?.disconnect();
        toggle?.removeEventListener("click", handleToggle);
        document.removeEventListener("visibilitychange", handleEnvironmentChange);
        mobileViewport.removeEventListener?.("change", handleEnvironmentChange);
        reduceMotion.removeEventListener?.("change", handleEnvironmentChange);
        delete story.fsV2StorySelect;
        delete story.fsV2StoryCleanup;
        delete story.dataset.fsStoryReady;
      };

      setActive(activeIndex);
      updateToggle();
      schedule();
    });
  };

  const init = (scope = document) => {
    reveal(scope);
    initFooter(scope);
    initAtlas(scope);
    initStorySlideshows(scope);
  };

  const unload = (scope) => {
    collect(scope, SELECTOR).forEach((node) => observer?.unobserve(node));
    collect(scope, STORY_SELECTOR).forEach((story) => story.fsV2StoryCleanup?.());
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => unload(event.target));
  document.addEventListener("shopify:block:select", (event) => {
    collect(event.target, SELECTOR).forEach(show);
    if (event.target.matches?.(SELECTOR)) show(event.target);

    const slide = event.target.closest?.("[data-fs-v2-atlas-slide]");
    const track = slide?.closest?.("[data-fs-v2-atlas-track]");
    if (slide && track && window.matchMedia("(max-width: 749px)").matches) {
      scrollAtlasTo(track, slide, "auto");
    }

    const storySlide = event.target.closest?.("[data-fs-v2-story-slide]");
    const story = storySlide?.closest?.(STORY_SELECTOR);
    story?.fsV2StorySelect?.(storySlide);
  });

  reduceMotion.addEventListener?.("change", () => init(document));

  window.FishingSections = { version: VERSION, init };
})();
